import { type ChangeEvent, type FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  AuthUser,
  getAccessToken,
  getDefaultAuthenticatedRoute,
  getRefreshToken,
  getStoredUser,
  hasRole,
  logoutUser,
  refreshStoredUser,
  storeAuthSession,
} from '../utils/auth';
import { resolveCmReferralRoute } from '../utils/cmReferralRoute';
import { trackProductEvent } from '../utils/telemetry';
import InputMask from '@mona-health/react-input-mask';
import { CAREGIVER_PRIMARY_LANGUAGE_OPTIONS } from '../../shared/nexusPicklists';

class ApiRequestError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

function authFailureProperties(error: unknown): Record<string, string | number> {
  return error instanceof ApiRequestError
    ? { statusCode: error.status, ...(error.code ? { reason: error.code } : {}) }
    : { reason: 'client_or_network_error' };
}

async function apiRequest<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(data.message || 'Request failed', response.status, data.code);
  }

  return data as T;
}

const LEAD_GENDER_OPTIONS = ['Male', 'Female'];

interface LeadFormState {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  primaryLanguage: string;
}

const EMPTY_LEAD_FORM: LeadFormState = {
  firstName: '',
  lastName: '',
  email: '',
  gender: '',
  primaryLanguage: '',
};

interface StartProps {
  cmReferralAccountId?: string;
}

function Start({ cmReferralAccountId = '' }: StartProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isQeMode = (searchParams.get('qe') || searchParams.get('QE') || '').toLowerCase() === 'true';
  const isRmMode = (searchParams.get('rm') || searchParams.get('RM') || '').toLowerCase() === 'true';
  const cmReferralRoute = resolveCmReferralRoute(location.search, cmReferralAccountId);
  const isCmMode = cmReferralRoute.isCmMode;
  const raParam = cmReferralRoute.referringAccountId;
  const entryAuthFlow = isCmMode ? 'cm_referral' : isRmMode ? 'rm_login' : 'caregiver_login';
  const logoutRoute = cmReferralAccountId ? '/mrnyc' : '/start';
  const forceFreshLogin = Boolean(cmReferralAccountId);
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [rmLoginId, setRmLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [leadIntakeToken, setLeadIntakeToken] = useState('');
  const [leadForm, setLeadForm] = useState<LeadFormState>(EMPTY_LEAD_FORM);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isPasswordLoginMode, setIsPasswordLoginMode] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthUser | null>(() => (
    forceFreshLogin ? null : getStoredUser()
  ));
  const [authRefreshing, setAuthRefreshing] = useState(() => (
    forceFreshLogin ? false : Boolean(getStoredUser())
  ));
  const autoLoginStartedRef = useRef(false);
  const freshLoginResetStartedRef = useRef(false);

  useLayoutEffect(() => {
    if (!forceFreshLogin || freshLoginResetStartedRef.current) return;
    freshLoginResetStartedRef.current = true;
    if (getStoredUser() || getRefreshToken() || getAccessToken()) {
      void logoutUser();
    }
  }, [forceFreshLogin]);

  useEffect(() => {
    trackProductEvent('auth_landing_viewed', {
      flow: entryAuthFlow,
      path: location.pathname,
      hasReferralAccount: Boolean(raParam),
    });
  }, [entryAuthFlow, location.pathname, raParam]);

  const hasQeLoginParams = () => {
    const params = new URLSearchParams(window.location.search);
    const trimmedId = (params.get('id') || '').trim();
    return (params.get('qe') || params.get('QE'))?.toLowerCase() === 'true' && trimmedId.length > 0;
  };

  const hasPortalPinLoginParams = () => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('mobile') && params.get('pin'));
  };

  useEffect(() => {
    let cancelled = false;

    async function refreshAndRedirect() {
      if ((hasPortalPinLoginParams() || hasQeLoginParams()) && !autoLoginStartedRef.current) {
        setAuthRefreshing(false);
        return;
      }

      if (!authenticatedUser) {
        setAuthRefreshing(false);
        return;
      }

      setAuthRefreshing(true);
      let nextUser = authenticatedUser;
      try {
        nextUser = await refreshStoredUser() ?? authenticatedUser;
      } catch {
        nextUser = getStoredUser() ?? authenticatedUser;
      }

      if (cancelled) return;
      setAuthenticatedUser(nextUser);
      const defaultRoute = getDefaultAuthenticatedRoute(nextUser);
      if (defaultRoute !== '/start') {
        navigate(defaultRoute, { replace: true });
      }
      setAuthRefreshing(false);
    }

    void refreshAndRedirect();
    return () => {
      cancelled = true;
    };
  }, [authenticatedUser?.id, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const qe = (searchParams.get('qe') || searchParams.get('QE') || '').toLowerCase();
    const qeLoginId = searchParams.get('id')?.trim() || '';

    if (qe !== 'true' || !qeLoginId || autoLoginStartedRef.current) {
      return;
    }

    let cancelled = false;
    autoLoginStartedRef.current = true;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    apiRequest<{
      accessToken: string;
      refreshToken: string;
      user: AuthUser;
    }>('/api/auth/qe-login', { qeLoginId })
      .then((result) => {
        if (cancelled) return;
        storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
        setAuthenticatedUser(result.user);
        trackProductEvent('login_succeeded', { flow: 'quick_entry' });
        navigate('/qe-landing', { replace: true });
      })
      .catch((err) => {
        trackProductEvent('login_failed', {
          flow: 'quick_entry',
          ...authFailureProperties(err),
        });
        if (!cancelled) setError(err instanceof Error ? err.message : 'Invalid QE access code.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [logoutRoute, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const mobile = searchParams.get('mobile')?.replace(/\D/g, '') || '';
    const pin = searchParams.get('pin')?.trim() || '';

    if (mobile) {
      setPhone(mobile);
    }

    if (!mobile || !pin || autoLoginStartedRef.current) {
      return;
    }

    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('mobile');
    cleaned.delete('pin');
    navigate(
      { pathname: location.pathname, search: cleaned.toString() ? `?${cleaned.toString()}` : '' },
      { replace: true }
    );

    let cancelled = false;
    autoLoginStartedRef.current = true;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    apiRequest<{
      accessToken: string;
      refreshToken: string;
      user: AuthUser;
    }>('/api/auth/portal-pin-login', { mobile, pin })
      .then((result) => {
        if (cancelled) return;
        storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
        setAuthenticatedUser(result.user);
        trackProductEvent('login_succeeded', {
          flow: 'portal_pin',
          caregiverStatus: result.user.nexusCaregiver?.status,
          portalStatus: result.user.nexusCaregiver?.portalStatus,
        });
        const defaultRoute = getDefaultAuthenticatedRoute(result.user);
        if (defaultRoute !== '/start') {
          navigate(defaultRoute, { replace: true });
        }
      })
      .catch((err) => {
        trackProductEvent('login_failed', {
          flow: 'portal_pin',
          ...authFailureProperties(err),
        });
        if (!cancelled) setError(err instanceof Error ? err.message : 'Invalid or expired portal PIN.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, logoutRoute, navigate]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('resetToken');
    if (!token) return;

    let cancelled = false;
    apiRequest<{ valid: boolean }>('/api/auth/validate-reset-token', { token })
      .then((result) => {
        if (cancelled) return;
        if (result.valid) {
          setResetToken(token);
          setResetDialogOpen(true);
          setIsPasswordLoginMode(true);
        } else {
          setError('Invalid or expired reset token');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to validate reset token');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const updateLeadForm = (field: keyof LeadFormState, value: string) => {
    setLeadForm((current) => ({ ...current, [field]: value }));
  };

  const resetLeadIntake = () => {
    setLeadIntakeToken('');
    setLeadForm(EMPTY_LEAD_FORM);
    setIsCodeSent(false);
    setVerificationCode('');
  };

  const handleSendCode = async () => {
    setError(null);
    setSuccessMessage(null);
    resetLeadIntake();
    setLoading(true);

    try {
      const normalizedPhone = phone.replace(/\D/g, '');

      if (normalizedPhone.length !== 10) {
        trackProductEvent('verification_code_request_failed', {
          flow: entryAuthFlow,
          reason: 'invalid_phone',
        });
        setError(t('register.phoneDigitsError'));
        setLoading(false);
        return;
      }

      if (isRmMode) {
        trackProductEvent('rm_login_started', { flow: entryAuthFlow });
        setPhone(normalizedPhone);
        setIsCodeSent(true);
        return;
      }

      await apiRequest('/api/auth/send-code', {
        phone: normalizedPhone,
        ...(isCmMode ? { cm: true } : {}),
      });
      trackProductEvent('verification_code_sent', { flow: entryAuthFlow });
      setIsCodeSent(true);
      setSuccessMessage(t('register.codeSent'));
    } catch (err) {
      trackProductEvent('verification_code_request_failed', {
        flow: entryAuthFlow,
        ...authFailureProperties(err),
      });
      setError(err instanceof Error ? err.message : t('register.sendCodeError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const normalizedPhone = phone.replace(/\D/g, '');
      const normalizedCode = verificationCode.replace(/\D/g, '');

      if (!isRmMode && normalizedCode.length !== 6) {
        trackProductEvent('login_failed', {
          flow: entryAuthFlow,
          reason: 'invalid_code_length',
        });
        setError(t('register.codeDigitsError'));
        setLoading(false);
        return;
      }

      if (isRmMode && !rmLoginId.trim()) {
        trackProductEvent('login_failed', {
          flow: entryAuthFlow,
          reason: 'missing_rm_login_id',
        });
        setError('RM Login ID is required');
        setLoading(false);
        return;
      }

      const result = await apiRequest<{
        accessToken?: string;
        refreshToken?: string;
        user?: AuthUser;
        leadIntakeRequired?: boolean;
        leadIntakeToken?: string;
      }>(isRmMode ? '/api/auth/rm-login' : '/api/auth/verify-code', {
        phone: normalizedPhone,
        ...(!isRmMode ? { code: normalizedCode } : {}),
        ...(isRmMode ? { rmLoginId: rmLoginId.trim() } : {}),
        ...(!isRmMode && isCmMode && raParam ? { cm: 'true', ra: raParam } : {}),
      });

      if (result.leadIntakeRequired && result.leadIntakeToken) {
        trackProductEvent('caregiver_intake_required', { flow: entryAuthFlow });
        setLeadIntakeToken(result.leadIntakeToken);
        setIsCodeSent(false);
        setVerificationCode('');
        setSuccessMessage('Phone verified. Enter your information to continue.');
        return;
      }

      if (!result.accessToken || !result.refreshToken || !result.user) {
        throw new Error(t('register.verifyCodeError'));
      }

      storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
      setAuthenticatedUser(result.user);
      trackProductEvent('login_succeeded', {
        flow: entryAuthFlow,
        caregiverStatus: result.user.nexusCaregiver?.status,
        portalStatus: result.user.nexusCaregiver?.portalStatus,
      });
      setSuccessMessage(t('register.codeVerified'));
    } catch (err) {
      trackProductEvent('login_failed', {
        flow: entryAuthFlow,
        ...authFailureProperties(err),
      });
      setError(err instanceof Error ? err.message : t('register.verifyCodeError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLeadFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!leadIntakeToken) {
      trackProductEvent('caregiver_intake_failed', {
        flow: entryAuthFlow,
        reason: 'missing_intake_session',
      });
      setError('Your verification session has expired. Please verify your phone again.');
      return;
    }
    if (!leadForm.firstName.trim() || !leadForm.lastName.trim() || !leadForm.email.trim() || !leadForm.gender || !leadForm.primaryLanguage) {
      trackProductEvent('caregiver_intake_failed', {
        flow: entryAuthFlow,
        reason: 'missing_required_fields',
      });
      setError('First name, last name, email, gender, and primary language are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadForm.email.trim())) {
      trackProductEvent('caregiver_intake_failed', {
        flow: entryAuthFlow,
        reason: 'invalid_email',
      });
      setError('Invalid email format');
      return;
    }

    setLoading(true);
    try {
      const result = await apiRequest<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>('/api/auth/cm-lead', {
        leadIntakeToken,
        ...leadForm,
        email: leadForm.email.trim(),
        firstName: leadForm.firstName.trim(),
        lastName: leadForm.lastName.trim(),
      });
      setLeadIntakeToken('');
      setLeadForm(EMPTY_LEAD_FORM);
      storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
      setAuthenticatedUser(result.user);
      trackProductEvent('caregiver_intake_completed', {
        flow: entryAuthFlow,
        caregiverStatus: result.user.nexusCaregiver?.status,
        portalStatus: result.user.nexusCaregiver?.portalStatus,
      });
      setSuccessMessage('Thank you. Your information has been submitted.');
    } catch (err) {
      trackProductEvent('caregiver_intake_failed', {
        flow: entryAuthFlow,
        ...authFailureProperties(err),
      });
      if (err instanceof ApiRequestError && err.code === 'INVALID_CM_LEAD_INTAKE_TOKEN') {
        resetLeadIntake();
        setError('Your verification session has expired. Please verify your phone again.');
        return;
      }
      setError(err instanceof Error ? err.message : 'There was a problem with your application. Please contact the office for assistance.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.length !== 10 || !password) {
        trackProductEvent('login_failed', {
          flow: 'password',
          reason: 'missing_credentials',
        });
        setError('Phone and password are required');
        setLoading(false);
        return;
      }

      const result = await apiRequest<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>('/api/auth/password-login', {
        phone: normalizedPhone,
        password,
      });

      storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
      setAuthenticatedUser(result.user);
      trackProductEvent('login_succeeded', { flow: 'password' });
    } catch (err) {
      trackProductEvent('login_failed', {
        flow: 'password',
        ...authFailureProperties(err),
      });
      setError(err instanceof Error ? err.message : 'Password login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQeLogin = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const qeLoginId = password.trim();
      if (!qeLoginId) {
        trackProductEvent('login_failed', {
          flow: 'quick_entry',
          reason: 'missing_access_code',
        });
        setError('Please enter your access code.');
        setLoading(false);
        return;
      }

      const result = await apiRequest<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>('/api/auth/qe-login', { qeLoginId });

      storeAuthSession(result.accessToken, result.refreshToken, result.user, { logoutRoute });
      setAuthenticatedUser(result.user);
      trackProductEvent('login_succeeded', { flow: 'quick_entry' });
      navigate('/qe-landing', { replace: true });
    } catch (err) {
      trackProductEvent('login_failed', {
        flow: 'quick_entry',
        ...authFailureProperties(err),
      });
      setError(err instanceof Error ? err.message : 'Invalid QE access code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const normalizedPhone = (forgotPasswordPhone || phone).replace(/\D/g, '');
      await apiRequest('/api/auth/forgot-password', { phone: normalizedPhone });
      setForgotPasswordOpen(false);
      setForgotPasswordPhone('');
      setSuccessMessage('If there is a user associated with this phone number, password reset instructions will be sent.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    setError(null);
    setSuccessMessage(null);
    if (resetPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await apiRequest('/api/auth/set-password', { token: resetToken, password: resetPassword });
      setResetDialogOpen(false);
      setResetToken('');
      setResetPassword('');
      setResetPasswordConfirm('');
      setSuccessMessage('Password set successfully. You can now log in with your password.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPasswordSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!loading) void handleSetPassword();
  };

  const handleResendCode = async () => {
    setVerificationCode('');
    await handleSendCode();
  };

  const handleLogout = async () => {
    const nextRoute = await logoutUser();
    setAuthenticatedUser(null);
    setPhone('');
    setVerificationCode('');
    setRmLoginId('');
    setPassword('');
    resetLeadIntake();
    setIsCodeSent(false);
    setIsPasswordLoginMode(false);
    setError(null);
    setSuccessMessage(null);
    navigate(nextRoute, { replace: true });
  };

  return (
    <Box sx={{ width: '100%', minHeight: 'calc(100vh - 64px)', py: { xs: 2, md: 5 } }}>
      <Container maxWidth="sm">
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#fff',
          borderRadius: 1,
        }}
      >
        <Box paddingBottom="10px" textAlign="center" mb={2}>
          <Box
            component="img"
            src="/images/logo/evo-logo-link.png"
            alt="Link Logo"
            sx={{
              maxWidth: '55%',
              minWidth: 190,
              height: 'auto',
              mt: 2.5,
              mb: 1,
            }}
          />
        </Box>

        {authenticatedUser ? (
          <Box mb={2}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('register.signedIn')}
            </Alert>
            {authRefreshing && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {t('start.refreshingPortalStatus', 'Refreshing portal status...')}
                </Typography>
              </Stack>
            )}
            <Typography variant="h6" component="h1">
              {authenticatedUser.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {authenticatedUser.username}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              {authenticatedUser.roles.map((role) => (
                <Chip key={role.id} label={role.name} size="small" color="primary" variant="outlined" />
              ))}
            </Stack>

            {authenticatedUser.nexusCaregiver && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">
                  {authenticatedUser.nexusCaregiver.status} / {authenticatedUser.nexusCaregiver.portalStatus}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('start.nexusId', 'Nexus ID')}: {authenticatedUser.nexusCaregiver.nexusId}
                </Typography>
              </>
            )}

            {!authRefreshing && (
              <Stack spacing={1.5} sx={{ mt: 3 }}>
                {hasRole(authenticatedUser, 'app') && (
                  <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/application')}>
                    {t('start.continueApplication', 'Continue Application')}
                  </Button>
                )}
                {hasRole(authenticatedUser, 'hha') && (
                  <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/onboarding')}>
                    {t('start.continueOnboarding', 'Continue Onboarding')}
                  </Button>
                )}
                {(hasRole(authenticatedUser, 'nurse') || hasRole(authenticatedUser, 'nurse-admin')) && (
                  <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/nursing-assessment')}>
                    Nursing Assessment
                  </Button>
                )}
                {hasRole(authenticatedUser, 'referral') && (
                  <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/referral')}>
                    Referral Portal
                  </Button>
                )}
              </Stack>
            )}

            <Button variant="outlined" color="primary" fullWidth sx={{ mt: 3 }} onClick={handleLogout}>
              {t('register.logoutButton')}
            </Button>
          </Box>
        ) : isQeMode ? (
          <Box mb={0}>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
              variant="outlined"
              disabled={loading}
              autoComplete="current-password"
              inputMode="numeric"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleQeLogin();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2, p: 2 }}
              onClick={handleQeLogin}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
          </Box>
        ) : leadIntakeToken ? (
          <Box component="form" onSubmit={handleLeadFormSubmit} mb={0}>
            <TextField
              name="firstName"
              label="First Name"
              value={leadForm.firstName}
              onChange={(event) => updateLeadForm('firstName', event.target.value)}
              fullWidth
              variant="outlined"
              disabled={loading}
              autoComplete="given-name"
            />
            <TextField
              name="lastName"
              label="Last Name"
              value={leadForm.lastName}
              onChange={(event) => updateLeadForm('lastName', event.target.value)}
              fullWidth
              variant="outlined"
              disabled={loading}
              autoComplete="family-name"
              sx={{ mt: 2 }}
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={leadForm.email}
              onChange={(event) => updateLeadForm('email', event.target.value)}
              fullWidth
              variant="outlined"
              disabled={loading}
              autoComplete="email"
              sx={{ mt: 2 }}
            />
            <TextField
              name="gender"
              label="Gender"
              value={leadForm.gender}
              onChange={(event) => updateLeadForm('gender', event.target.value)}
              fullWidth
              select
              variant="outlined"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {LEAD_GENDER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="primaryLanguage"
              label="Primary Language"
              value={leadForm.primaryLanguage}
              onChange={(event) => updateLeadForm('primaryLanguage', event.target.value)}
              fullWidth
              select
              variant="outlined"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {CAREGIVER_PRIMARY_LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2, p: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
            </Button>
            <Button
              type="button"
              variant="text"
              fullWidth
              sx={{ mt: 1 }}
              disabled={loading}
              onClick={() => {
                resetLeadIntake();
                setError(null);
                setSuccessMessage(null);
              }}
            >
              Verify phone again
            </Button>
          </Box>
        ) : (
          <Box mb={0}>
            <InputMask
              mask="999 999 9999"
              value={phone}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPhone(event.target.value)}
              disabled={(isCodeSent && !isPasswordLoginMode) || loading}
              maskPlaceholder={null}
            >
              <TextField
                label={t('register.mobilePhoneNumberLabel')}
                fullWidth
                variant="outlined"
                inputMode="tel"
                autoComplete="tel"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isCodeSent && !isPasswordLoginMode) {
                    event.preventDefault();
                    handleSendCode();
                  } else if (event.key === 'Enter' && isPasswordLoginMode) {
                    event.preventDefault();
                    handlePasswordLogin();
                  }
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#333',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </InputMask>

            {isPasswordLoginMode && !isRmMode && !isCodeSent && (
              <>
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  autoComplete="current-password"
                  sx={{ mt: 2 }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handlePasswordLogin();
                    }
                  }}
                />
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      setForgotPasswordPhone(phone);
                      setForgotPasswordOpen(true);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: '#1976d2',
                      fontSize: '0.875rem',
                      lineHeight: 1.2,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>
              </>
            )}

            {isCodeSent && (
              <>
                {isRmMode ? (
                  <TextField
                    label="Password"
                    value={rmLoginId}
                    onChange={(event) => setRmLoginId(event.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={loading}
                    autoComplete="off"
                    sx={{ mt: 2 }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleVerifyCode();
                      }
                    }}
                  />
                ) : (
                  <TextField
                    label={t('register.verificationCodeLabel')}
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={loading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    sx={{ mt: 2 }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleVerifyCode();
                      }
                    }}
                  />
                )}
              </>
            )}

            {!isCodeSent ? (
              isPasswordLoginMode && !isRmMode ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2, p: 2 }}
                    onClick={handlePasswordLogin}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                  </Button>
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => {
                        setIsPasswordLoginMode(false);
                        setPassword('');
                        setError(null);
                      }}
                      sx={{
                        cursor: 'pointer',
                        color: '#1976d2',
                        fontSize: '0.875rem',
                        lineHeight: 1.2,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Use Verification Code
                    </Link>
                  </Box>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2, p: 2 }}
                    onClick={handleSendCode}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : (isRmMode ? 'Referral Login' : t('register.sendVerificationCodeButton'))}
                  </Button>
                  {!isRmMode && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => {
                          setIsPasswordLoginMode(true);
                          setError(null);
                          setSuccessMessage(null);
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: '#1976d2',
                          fontSize: '0.875rem',
                          lineHeight: 1.2,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Password Login
                      </Link>
                    </Box>
                  )}
                </>
              )
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2, p: 2 }}
                  onClick={handleVerifyCode}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : (isRmMode ? 'Submit Password' : t('register.verifyCodeButton'))}
                </Button>
                {!isRmMode && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    {t('register.resendCodeButton')}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

        {successMessage && !authenticatedUser && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Dialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter your phone number to receive password reset instructions.
            </Typography>
            <InputMask
              mask="999 999 9999"
              value={forgotPasswordPhone}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setForgotPasswordPhone(event.target.value)}
              disabled={loading}
              maskPlaceholder={null}
            >
              <TextField
                label={t('register.mobilePhoneNumberLabel')}
                fullWidth
                inputMode="tel"
                autoComplete="tel"
              />
            </InputMask>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              If there is a user associated with this phone number, we will send an email with reset instructions.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForgotPasswordOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleForgotPassword} disabled={loading} variant="contained">
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} fullWidth maxWidth="xs">
          <Box component="form" onSubmit={handleSetPasswordSubmit}>
            <DialogTitle>Set Password</DialogTitle>
            <DialogContent>
              <TextField
                label="New Password"
                type="password"
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
                fullWidth
                autoComplete="new-password"
                sx={{ mt: 1 }}
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={resetPasswordConfirm}
                onChange={(event) => setResetPasswordConfirm(event.target.value)}
                fullWidth
                autoComplete="new-password"
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={() => setResetDialogOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} variant="contained">
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Set Password'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Paper>
      </Container>
    </Box>
  );
}

export default Start;
